import { apiClient } from "./client";
import type {
    AddressResponse,
    BulkHideAddressesRequest,
    CreateAddressRequest,
    DuplicateAddressCandidate,
    FixUnderDeliveredAddressItem,
    HiddenAddressWithDeliveries,
    MergeAddressesRequest,
    MergeAddressesResponse,
    UnderDeliveredAddress,
    UpdateAddressRequest
} from "./addresses/types";

/**
 * Get a single address by ID
 */
export const getAddress = async (id: string): Promise<AddressResponse> => {
    const { data } = await apiClient.get<AddressResponse>(`/addresses/${id}`);
    return data;
};

/**
 * Create a new address
 */
export const createAddress = async (request: CreateAddressRequest): Promise<AddressResponse> => {
    const { data } = await apiClient.post<AddressResponse>("/addresses", request);
    return data;
};

/**
 * Update an existing address
 */
export const updateAddress = async (
    id: string,
    request: UpdateAddressRequest
): Promise<AddressResponse> => {
    const { data } = await apiClient.patch<AddressResponse>(`/addresses/${id}`, request);
    return data;
};

/**
 * Delete (soft delete) an address
 */
export const deleteAddress = async (id: string): Promise<void> => {
    await apiClient.delete(`/addresses/${id}`);
};

/**
 * Hide or show multiple addresses in one request
 */
export const bulkHideAddresses = async (
    request: BulkHideAddressesRequest
): Promise<{ updatedCount: number }> => {
    const { data } = await apiClient.post<{ updatedCount: number }>("/addresses/bulk-hide", request);
    return data;
};

/**
 * Merge multiple address points into one (sums delivery units, hides the rest)
 */
export const mergeAddresses = async (
    request: MergeAddressesRequest
): Promise<MergeAddressesResponse> => {
    const { data } = await apiClient.post<MergeAddressesResponse>("/addresses/merge", request);
    return data;
};

/**
 * List hidden addresses that still have non-deleted deliveries (admin cleanup tool)
 */
export const getHiddenAddressesWithDeliveries = async (): Promise<HiddenAddressWithDeliveries[]> => {
    const { data } = await apiClient.get<HiddenAddressWithDeliveries[]>("/addresses/hidden-with-deliveries");
    return data;
};

/**
 * Soft-delete all remaining deliveries for a hidden address, across all campaigns
 */
export const purgeDeliveriesForHiddenAddress = async (id: string): Promise<{ deletedCount: number }> => {
    const { data } = await apiClient.delete<{ deletedCount: number }>(`/addresses/${id}/deliveries`);
    return data;
};

/**
 * Soft-delete all remaining deliveries for multiple hidden addresses in one request
 */
export const purgeDeliveriesForHiddenAddresses = async (
    addressPointIds: string[]
): Promise<{ deletedCount: number; purgedAddressCount: number }> => {
    const { data } = await apiClient.delete<{ deletedCount: number; purgedAddressCount: number }>(
        "/addresses/deliveries",
        { data: addressPointIds }
    );
    return data;
};

/**
 * List visible addresses where more delivery units (boxes) exist than what's
 * been logged as delivered in a campaign (admin diagnostic tool)
 */
export const getUnderDeliveredAddresses = async (): Promise<UnderDeliveredAddress[]> => {
    const { data } = await apiClient.get<UnderDeliveredAddress[]>("/addresses/under-delivered");
    return data;
};

/**
 * Bumps the logged delivery quantity up to the address's current box count for
 * one or more (address, campaign) rows, fixing an under-delivery mismatch
 */
export const fixUnderDeliveredAddresses = async (
    items: FixUnderDeliveredAddressItem[]
): Promise<{ fixedRowCount: number; updatedDeliveryCount: number }> => {
    const { data } = await apiClient.post<{ fixedRowCount: number; updatedDeliveryCount: number }>(
        "/addresses/under-delivered/fix",
        items
    );
    return data;
};

/**
 * List candidate pairs of nearby addresses on different streets that might be the
 * same mailbox registered twice (e.g. a corner property with addresses on two
 * streets). Heuristic only — for manual review on a map, not a confirmed list.
 */
export const getDuplicateAddressCandidates = async (): Promise<DuplicateAddressCandidate[]> => {
    const { data } = await apiClient.get<DuplicateAddressCandidate[]>("/addresses/duplicate-candidates");
    return data;
};

/**
 * Marks a duplicate-address candidate pair as "not the same address" so it stops
 * showing up in the report for everyone (stored server-side, not per-browser).
 */
export const dismissDuplicateAddressCandidate = async (addressId1: string, addressId2: string): Promise<void> => {
    await apiClient.post("/addresses/duplicate-candidates/dismiss", { addressId1, addressId2 });
};

/**
 * Un-dismisses every previously dismissed duplicate-address candidate pair.
 */
export const clearDismissedDuplicateAddressCandidates = async (): Promise<{ clearedCount: number }> => {
    const { data } = await apiClient.delete<{ clearedCount: number }>("/addresses/duplicate-candidates/dismissed");
    return data;
};